import React from 'react';
import { Field, ErrorMessage } from 'formik';
import { useDropzone } from 'react-dropzone';
import API_URL from '../config';
import {
    FormSection,
    Input,
    DropzoneContainer,
    FileList,
    FileListItem,
    RemoveButton,
} from './Dashboard.styles';

interface ProjectSectionFormProps {
    index: number;
    section: any;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

const ProjectSectionForm: React.FC<ProjectSectionFormProps> = ({ index, section, setFieldValue }) => {
    const { getRootProps: getPlanRootProps, getInputProps: getPlanInputProps } = useDropzone({
        onDrop: (acceptedFiles) => {
            setFieldValue(`sections.${index}.plan`, acceptedFiles[0]);
        },
        maxFiles: 1,
    });

    const { getRootProps: getImagesRootProps, getInputProps: getImagesInputProps } = useDropzone({
        onDrop: (acceptedFiles) => {
            setFieldValue(`sections.${index}.images`, [...section.images, ...acceptedFiles]);
        },
    });

    const removePlan = () => {
        setFieldValue(`sections.${index}.plan`, null);
    };

    const removeImage = (fileToRemove: File | string) => {
        const newFiles = section.images.filter((file: File | string) => file !== fileToRemove);
        setFieldValue(`sections.${index}.images`, newFiles);
    };

    return (
        <FormSection>
            <h4>Section {index + 1}</h4>
            <Field as={Input} name={`sections.${index}.sectionName`} placeholder="Section Name" />
            <ErrorMessage name={`sections.${index}.sectionName`} component="div" />
            <Field as={Input} name={`sections.${index}.afgeperstMet`} placeholder="Afgeperst Met" />
            <ErrorMessage name={`sections.${index}.afgeperstMet`} component="div" />
            <Field as={Input} name={`sections.${index}.hoeveelheidDruk`} type="number" placeholder="Hoeveelheid Druk" />
            <ErrorMessage name={`sections.${index}.hoeveelheidDruk`} component="div" />
            <Field as={Input} name={`sections.${index}.buitenTemperatuur`} type="number" placeholder="Buiten Temperatuur" />
            <ErrorMessage name={`sections.${index}.buitenTemperatuur`} component="div" />
            
            <h4>Plan</h4>
            <DropzoneContainer {...getPlanRootProps()}>
                <input {...getPlanInputProps()} />
                <p>Drag 'n' drop a plan image here, or click to select one</p>
            </DropzoneContainer>
            {section.plan && (() => {
                const plan = section.plan;
                let url = '';
                let name = '';

                if (plan instanceof File) {
                    url = URL.createObjectURL(plan);
                    name = plan.name;
                } else if (typeof plan === 'string') {
                    url = `${API_URL}/${plan}`;
                    name = plan;
                } else if (typeof plan === 'object' && plan.url) {
                    url = plan.url;
                    name = plan.key || 'plan';
                }

                return url ? (
                    <FileListItem>
                        <a href={url} target="_blank" rel="noopener noreferrer">
                            <img src={url} alt={name} width="50" height="50" />
                        </a>
                        <span>{name}</span>
                        <RemoveButton type="button" onClick={removePlan}>
                            Remove
                        </RemoveButton>
                    </FileListItem>
                ) : null;
            })()}

            <h4>Images</h4>
            <DropzoneContainer {...getImagesRootProps()}>
                <input {...getImagesInputProps()} />
                <p>Drag 'n' drop some files here, or click to select files</p>
            </DropzoneContainer>
            <FileList>
                {section.images.map((file: any, i: number) => {
                    let url = '';
                    let name = '';

                    if (file instanceof File) {
                        url = URL.createObjectURL(file);
                        name = file.name;
                    } else if (typeof file === 'string') {
                        url = `${API_URL}/${file}`;
                        name = file;
                    } else if (typeof file === 'object' && file.url) {
                        url = file.url;
                        name = file.key || `image-${i}`;
                    }

                    return url ? (
                        <FileListItem key={i}>
                            <a href={url} target="_blank" rel="noopener noreferrer">
                                <img src={url} alt={name} width="50" height="50" />
                            </a>
                            <span>{name}</span>
                            <RemoveButton type="button" onClick={() => removeImage(file)}>
                                Remove
                            </RemoveButton>
                        </FileListItem>
                    ) : null;
                })}
            </FileList>
        </FormSection>
    );
};

export default ProjectSectionForm;
